import type { TargetSchema, KnownMapping, HistoricalFile } from "@/types";

// ── Schemas ───────────────────────────────────────────────────

export const mockTargetSchemas: TargetSchema[] = [
  {
    id: "lloyds-5.2",
    name: "Lloyds Coverholder Reporting Standard",
    version: "5.2",
    description: "Lloyd's standard BDX reporting schema",
  },
  {
    id: "non-lloyds-demo-1.0",
    name: "Non-Lloyds Demo Schema",
    version: "1.0",
    description: "Demo schema used to validate non-Lloyds upload flow",
  },
];

// ── Known Mappings ────────────────────────────────────────────

export const mockKnownMappings: KnownMapping[] = [
  {
    mappingId: "1",
    sourceField: "Branch",
    targetField: "Branch",
    targetFieldId: "branch",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "j.smith",
    createdAt: "2025-01-15T10:00:00Z",
  },
  {
    mappingId: "2",
    sourceField: "Branch Reference",
    targetField: "Branch Reference",
    targetFieldId: "branch-reference",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "j.smith",
    createdAt: "2025-01-15T10:00:00Z",
  },
  {
    mappingId: "3",
    sourceField: "Broker",
    targetField: "Broker",
    targetFieldId: "broker",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "a.jones",
    createdAt: "2025-01-20T14:30:00Z",
  },
  {
    mappingId: "4",
    sourceField: "Policy Number",
    targetField: "Policy Number",
    targetFieldId: "policy-number",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "a.jones",
    createdAt: "2025-01-20T14:30:00Z",
  },
  {
    mappingId: "5",
    sourceField: "Sum Insured",
    targetField: "Total Sum Insured",
    targetFieldId: "total-sum-insured",
    schemaId: "lloyds-5.2",
    confidence: 0.95,
    approvedBy: "j.smith",
    createdAt: "2025-02-01T09:15:00Z",
  },
  {
    mappingId: "6",
    sourceField: "UMR",
    targetField: "Unique Market Reference",
    targetFieldId: "unique-market-reference",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "m.taylor",
    createdAt: "2025-02-10T11:00:00Z",
  },
  {
    mappingId: "7",
    sourceField: "Pol_No",
    targetField: "Policy Number",
    targetFieldId: "policy-number",
    schemaId: "lloyds-5.2",
    confidence: 0.98,
    approvedBy: "j.smith",
    createdAt: "2025-02-10T11:05:00Z",
  },
  {
    mappingId: "8",
    sourceField: "Ccy",
    targetField: "Original Currency",
    targetFieldId: "original-currency",
    schemaId: "lloyds-5.2",
    confidence: 1.0,
    approvedBy: "m.taylor",
    createdAt: "2025-02-10T11:10:00Z",
  },
];

// ── Historical Files (with per-mapping detail rows) ───────────

function buildMappingRows(
  fileBase: Omit<
    HistoricalFile,
    "sourceField" | "targetField" | "bdxType" | "mappingMethod" | "confidenceLevel" | "version"
  >,
  mappings: {
    source: string;
    target: string;
    method: "Green" | "Amber" | "Red";
    confidence: number;
  }[],
  bdxType: string,
  version: string | number
): HistoricalFile[] {
  return mappings.map((m) => ({
    ...fileBase,
    sourceField: m.source,
    targetField: m.target,
    bdxType,
    mappingMethod: m.method,
    confidenceLevel: m.confidence,
    version,
  }));
}

export const mockHistoricalFiles: HistoricalFile[] = [
  // ── File 1: Q4 2024 Risk Report (fully processed, good quality) ──
  ...buildMappingRows(
    {
      fileId: "f-001",
      fileName: "Q4_2024_Risk_Report.xlsx",
      uploadedBy: "j.smith",
      providerId: "prov-01",
      schemaId: "lloyds-5.2",
      status: "processed",
      qualityStatus: "pass",
      storagePath: "/uc/prov-01/f-001_Q4_2024_Risk_Report.xlsx",
      fileSize: 245_760,
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      checksum: "a1b2c3d4e5f6",
      createdAt: "2024-12-15T10:30:00Z",
      updatedAt: "2024-12-15T10:35:00Z",
    },
    [
      { source: "UMR", target: "Unique Market Reference", method: "Green", confidence: 1.0 },
      { source: "YOA", target: "Year of Account", method: "Green", confidence: 1.0 },
      {
        source: "Coverholder",
        target: "Coverholder / DCA Name",
        method: "Green",
        confidence: 0.98,
      },
      { source: "Inception", target: "Risk Inception Date", method: "Amber", confidence: 0.91 },
      { source: "Expiry", target: "Risk Expiry Date", method: "Amber", confidence: 0.89 },
      { source: "Ccy", target: "Original Currency", method: "Green", confidence: 1.0 },
      { source: "TSI", target: "Total Sum Insured", method: "Amber", confidence: 0.87 },
      { source: "Gross Premium", target: "Premium Amount", method: "Green", confidence: 1.0 },
      { source: "Pol_No", target: "Policy Number", method: "Green", confidence: 1.0 },
      { source: "Insured", target: "Insured Name", method: "Amber", confidence: 0.82 },
      { source: "Country", target: "Risk Location Country", method: "Amber", confidence: 0.78 },
      { source: "CoB_Code", target: "Class of Business", method: "Red", confidence: 0.62 },
    ],
    "Premium",
    "5.2"
  ),

  // ── File 2: Claims Jan 2025 (processed, some quality issues) ──
  ...buildMappingRows(
    {
      fileId: "f-002",
      fileName: "Claims_Jan_2025.xlsx",
      uploadedBy: "a.jones",
      providerId: "prov-02",
      schemaId: "lloyds-5.2",
      status: "processed",
      qualityStatus: "issues",
      storagePath: "/uc/prov-02/f-002_Claims_Jan_2025.xlsx",
      fileSize: 128_000,
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      checksum: "b2c3d4e5f6a1",
      createdAt: "2025-01-10T14:20:00Z",
      updatedAt: "2025-01-10T14:25:00Z",
    },
    [
      { source: "Claim_Ref", target: "Claim Number", method: "Green", confidence: 1.0 },
      { source: "Claim_Status", target: "Claim Status", method: "Green", confidence: 1.0 },
      { source: "Loss_Date_Field", target: "Loss Date", method: "Amber", confidence: 0.87 },
      { source: "Amount_Paid", target: "Paid Amount", method: "Amber", confidence: 0.92 },
      { source: "Reserve", target: "Outstanding Reserve", method: "Amber", confidence: 0.88 },
      { source: "Incurred_Total", target: "Incurred Amount", method: "Amber", confidence: 0.91 },
      { source: "Pol_No", target: "Policy Number", method: "Green", confidence: 1.0 },
      { source: "UMR", target: "Unique Market Reference", method: "Green", confidence: 1.0 },
      { source: "Settlement_Ccy", target: "Settlement Currency", method: "Green", confidence: 1.0 },
    ],
    "Claims",
    "5.2"
  ),

  // ── File 3: Premium Bordereaux Feb (uploaded, not yet processed) ──
  {
    fileId: "f-003",
    fileName: "Premium_Bordereaux_Feb.xlsx",
    uploadedBy: "j.smith",
    providerId: "prov-01",
    schemaId: "lloyds-5.2",
    status: "uploaded",
    qualityStatus: "pending",
    storagePath: "/uc/prov-01/f-003_Premium_Bordereaux_Feb.xlsx",
    fileSize: 512_000,
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    checksum: "c3d4e5f6a1b2",
    createdAt: "2025-02-05T09:00:00Z",
    updatedAt: "2025-02-05T09:05:00Z",
  },

  // ── File 4: Risk Data March (processed, failed quality) ──
  ...buildMappingRows(
    {
      fileId: "f-004",
      fileName: "Risk_Data_March_2025.xlsx",
      uploadedBy: "m.taylor",
      providerId: "prov-01",
      schemaId: "lloyds-5.2",
      status: "processed",
      qualityStatus: "fail",
      storagePath: "/uc/prov-01/f-004_Risk_Data_March_2025.xlsx",
      fileSize: 380_000,
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      checksum: "d4e5f6a1b2c3",
      createdAt: "2025-03-01T08:30:00Z",
      updatedAt: "2025-03-01T08:40:00Z",
    },
    [
      {
        source: "Market_Ref",
        target: "Unique Market Reference",
        method: "Amber",
        confidence: 0.85,
      },
      { source: "Account_Year", target: "Year of Account", method: "Amber", confidence: 0.79 },
      { source: "CH_Name", target: "Coverholder / DCA Name", method: "Red", confidence: 0.55 },
      { source: "Start_Date", target: "Risk Inception Date", method: "Amber", confidence: 0.82 },
      { source: "End_Date", target: "Risk Expiry Date", method: "Amber", confidence: 0.8 },
      { source: "Curr", target: "Original Currency", method: "Amber", confidence: 0.75 },
      { source: "Gross_Prem", target: "Premium Amount", method: "Amber", confidence: 0.88 },
    ],
    "Premium",
    "5.2"
  ),

  // ── File 5: Claims Q1 from provider 2 (processing) ──
  {
    fileId: "f-005",
    fileName: "Claims_Q1_2025_Batch2.json",
    uploadedBy: "a.jones",
    providerId: "prov-02",
    schemaId: "lloyds-5.2",
    status: "processing",
    qualityStatus: "pending",
    storagePath: "/uc/prov-02/f-005_Claims_Q1_2025_Batch2.json",
    fileSize: 96_000,
    mimeType: "application/json",
    checksum: "e5f6a1b2c3d4",
    createdAt: "2025-03-15T16:00:00Z",
    updatedAt: "2025-03-15T16:00:00Z",
  },
];

// ── Upload Simulation ─────────────────────────────────────────

export function simulateUpload(): Promise<{ fileId: string }> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ fileId: crypto.randomUUID() });
    }, 800);
  });
}
